import { usePremiumFeatureModal } from "features/Subscription/components/PremiumFeatureModal/usePremiumFeatureModal";
import useCredits from "features/User/hooks/useCredits";
import useSubscription from "features/User/hooks/useSubscription";
import { searchModeToCreditedFeature } from "features/User/utils";
import { SearchMode } from "helpers/api";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import useRequireSignIn from "hooks/useRequireSignIn";
import useSearchMode from "hooks/useSearchMode";
import { toast } from "react-toastify";
import { PremiumFeatureSecondaryButtonState } from "store/slices/subscription";
import { CreditedFeature } from "store/slices/subscription";

// A hook that handles the logic for checking if a user is able to perform a search based on their remaining credits and handles the redirects if not.
// Used in the various search inputs to eliminate duplicate logic.
function useCheckSearchCredits() {
  const { subscriptionTier } = useSubscription();
  const { searchModeCreditsRemaining, creditRefreshDate } = useCredits();
  const { redirectIfNotSignedIn } = useRequireSignIn();
  const { openPremiumFeatureModal } = usePremiumFeatureModal();
  const { changeSearchMode } = useSearchMode();
  const [labels] = useLabels("credits");

  // Returns true if the user is out of credits for this search mode.
  // If they are, redirects the user to the sign in page or show the out of credits modal depending on if they are signed in or not
  const redirectIfOutOfCredits = (
    searchMode: SearchMode,
    actionSource: MixpanelActionSource,
    sendAsQuickSearchButton?: PremiumFeatureSecondaryButtonState,
    onContinueOrRedirect?: () => void
  ) => {
    const creditsRemaining = searchModeCreditsRemaining(searchMode);
    if (creditsRemaining !== null && creditsRemaining <= 0) {
      // If the user is not signed in, redirect to the sign in page
      const isSignedIn = redirectIfNotSignedIn();
      if (!isSignedIn) {
        // if the user is out of credits but not signed in, we DO redirect, so we need to call the onContinueOrRedirect callback to e.g. close the filter sidebar.
        onContinueOrRedirect?.();
        return true;
      }

      // Handle Deep Search credit blocking based on subscription tier
      if (searchMode === SearchMode.PRO_RESEARCH) {
        // Only show upgrade modal for Pro users - they can upgrade to Deep
        if (subscriptionTier === "premium") {
          openPremiumFeatureModal({
            feature: CreditedFeature.DEEP_SEARCH,
            actionSource,
            secondaryButton: sendAsQuickSearchButton,
          });
          return true;
        }

        // For Team/Enterprise users, show toast (no individual upgrade path yet)
        toast.error(
          labels["out-of-deep-searches"].replace(
            "{RENEW_DATE}",
            creditRefreshDate
          )
        );
        changeSearchMode(SearchMode.PRO_ANALYSIS);
        return true;
      }

      // If the user is out of credits, show the out of credits modal
      openPremiumFeatureModal({
        feature: searchModeToCreditedFeature(searchMode),
        actionSource,
        secondaryButton: sendAsQuickSearchButton,
      });
      return true;
    }

    return false;
  };

  return { redirectIfOutOfCredits };
}

export default useCheckSearchCredits;
